import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { Button, Card } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useAuth } from "services/Auth";
import { createAPIClientAuth } from "utils/helper";
import { withAuth } from "@salespark/api-client";
import { downloadAutoSyncData } from "services/autoSync";
import { isOnline } from "utils/networkConnection";
const delay = (ms) => new Promise((res) => setTimeout(res, ms));
const getAutoSyncKey = (userId) => `md-auto-sync:${userId || "unknown"}`;

const AutoSync = () => {
  const { auth } = useAuth();
  const apiClient = useMemo(() => withAuth(createAPIClientAuth(auth.data)), [auth.data]);
  const history = useHistory();
  const [step, setStep] = useState("Aguarde...");
  const [isRunning, setIsRunning] = useState(false);
  const [isError, setIsError] = useState(false);

  const checkConnectivity = useCallback(async () => {
    setStep("A verificar a conectividade com o servidor...");
    return isOnline();
  }, []);

  const performAutoSync = useCallback(async () => {
    if (isRunning) {
      return;
    }

    setIsRunning(true);
    setIsError(false);
    const autoSyncKey = getAutoSyncKey(auth?.data?._id);
    try {
      // Check if already exists md-auto-sync for the current user with same token
      const existingToken = window.localStorage.getItem(autoSyncKey);
      if (existingToken === auth.data.token) {
        setStep("Sincronização já foi realizada anteriormente.");
        history.push("/home");
        return;
      }

      const connectivity = await checkConnectivity();
      if (!connectivity) {
        setStep("Conectividade falhou. Verifique a sua ligação à Internet.");
        setIsError(true);
        return;
      }

      setStep("A iniciar sincronização...");
      const downloadResponse = await downloadAutoSyncData({
        apiClient,
        onProgress: (message) => {
          if (typeof message === "string" && message.trim()) {
            setStep(message);
          }
        },
      });
      if (downloadResponse.status === false) {
        const failedTable = downloadResponse?.data?.table;
        if (failedTable) {
          setStep(`Transferência de dados falhou na tabela ${failedTable}. Tente novamente mais tarde.`);
          setIsError(true);
          return;
        }
        setStep("Transferência de dados falhou. Tente novamente mais tarde.");
        setIsError(true);
        return;
      }

      setStep("Sincronização concluída com sucesso!");
      await delay(1000);

      window.localStorage.setItem(autoSyncKey, auth.data.token);
      history.push("/home");
    } finally {
      setIsRunning(false);
    }
  }, [apiClient, auth?.data?._id, auth.data.token, checkConnectivity, history, isRunning]);

  useEffect(() => {
    performAutoSync();
  }, [performAutoSync]);

  return (
    <Card title="Sincronização de Dados Inicial" type="inner">
      <h3>
        {isError === false && <LoadingOutlined spin style={{ fontSize: 24 }} className="mr-2" />}
        {step}
      </h3>
      {isError && (
        <Button type="primary" onClick={performAutoSync} loading={isRunning} disabled={isRunning}>
          Tentar novamente
        </Button>
      )}
    </Card>
  );
};

export default AutoSync;
